<template>
  <v-card class="account-modal" v-click-outside="toggleAccountModal" style="z-index: 999;">
    <v-card-title class="px-4 pb-1 mb-1 d-flex justify-space-between align-start">
      <div class="d-flex">
        <v-avatar class="mt-1 initials-avatar" rounded size="25" color="primary">{{ userInitials }}</v-avatar>
        <div class="d-flex flex-column">
          <span class="subtitle-2 ml-2">{{ user.display_name }}</span>
          <span class="caption email ml-2">{{ user.email }}</span>
        </div>
      </div>
      <div>
        <v-btn icon>
          <v-icon size="24">{{ icons.mdiCog }}</v-icon>
        </v-btn>
      </div>
    </v-card-title>
    <v-divider></v-divider>
    <v-card-text class="pb-0">
      <v-text-field class="fixed-search-bar"  autofocus outlined dense placeholder="Search" :append-icon="icons.mdiMagnify" v-model="searchQuery"></v-text-field>
      <div class="scrollable-card">
        <v-radio-group v-model="selectedSpace" class="ma-0 pa-0">
          <div v-for="space in extendedFilteredSpaces" :key="space.space_uuid" class="d-flex my-1 align-center justify-space-between">
            <div class="d-flex mb-2">
              <v-avatar class="white--text font-weight-bold space-avatar" color="primary" size="32">
                {{ space.space_name[0].toUpperCase() }}
              </v-avatar>
              <div class="d-flex flex-column ml-2 mt-1" style="word-break: break-all;">
                <span class="subtitle-2 text-secondary" v-if="space.space_type === 'seven'">{{ space.space_name + ` (${space.space_type} - ${space.space_seven_datacenter})`}}</span>
                <span class="subtitle-2 text-secondary" v-else-if="space.space_type === 'vision'">{{ space.space_name + ` (${space.space_type} - ${space.space_vision_datacenter})`}}</span>
                <span class="subtitle-2 text-secondary" v-else>{{ space.space_name + ` (${space.space_type})`}}</span>
              </div>
            </div>
            <v-radio :ripple="false" class="mb-4 custom-radio" :value="space.space_uuid" :on-icon="icons.mdiCheckCircle"></v-radio>
          </div>
        </v-radio-group>
      </div>
    </v-card-text>
    <v-card-actions class="fixed-sign-out">
      <v-btn @click="logout" class="text-capitalize subtitle-2 font-weight-regular sign-out-btn my-0 py-1" outlined depressed>Sign out</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { mdiCheckCircle, mdiCog, mdiMagnify } from "@mdi/js";


export default {
  name: "AccountModal",
  data: () => ({
    searchQuery: '',
    selectedSpace: null,
    icons: {
      mdiCog: mdiCog,
      mdiMagnify: mdiMagnify,
      mdiCheckCircle: mdiCheckCircle
    },
    itemHeight: 64, // Adjust based on your item height
    maxVisibleItems: 10 // Adjust based on your maximum visible items
  }),
  computed: {
    ...mapGetters({
      user: "accounts/getUser",
      currentSpace: "global/getSpace",
      getUser: 'accounts/getUser'
    }),
     userInitials(){
      const u = (this.getUser && typeof this.getUser === 'object') ? this.getUser : null;

      const raw =
        u?.display_name ||
        u?.full_name ||
        u?.username ||
        u?.email ||
        '';

      if (!raw) return '';

      // Strip email domain if present
      const base = String(raw).trim().replace(/@.*/, '');

      // Split on common separators
      const parts = (base.includes(' '))
        ? base.split(/\s+/)
        : base.split(/[._-]/);

      const initials = parts
        .filter(Boolean)
        .map(p => p.charAt(0).toUpperCase());

      // Reverse order if at least 2 parts
      return initials.length > 1
        ? [initials[1], initials[0]].join('')
        : initials[0] || '';
    },
    filteredSpaces() {
    const query = this.searchQuery.toLowerCase();
    return this.user.spaces.filter(space =>
      space.space_name.toLowerCase().includes(query) ||
      space.space_type.toLowerCase().includes(query)
    );
  },
  extendedFilteredSpaces() {
    // Filter spaces based on the search query.
    let filteredSpaces = this.filteredSpaces;

    // Always ensure the current space is added to the top if search is cleared.
    const currentSpaceIndex = this.user.spaces.findIndex(space => space.space_uuid === this.currentSpace);
    const currentSpaceObject = this.user.spaces[currentSpaceIndex];

    // If search query is not empty, decide how to handle the selected and current spaces.
    if (this.searchQuery !== '') {
      const selectedIndex = filteredSpaces.findIndex(space => space.space_uuid === this.selectedSpace);

      if (selectedIndex > -1) {
        // Move the selected space to the front of the array if found in the filtered list.
        const [selectedItem] = filteredSpaces.splice(selectedIndex, 1);
        filteredSpaces.unshift(selectedItem);
      } else if (this.selectedSpace) {
        // If the selected space is not in the filtered results, find it and conditionally add it.
        const selectedSpaceObj = this.user.spaces.find(space => space.space_uuid === this.selectedSpace);
        if (selectedSpaceObj) {
          filteredSpaces.unshift(selectedSpaceObj);
        }
      }
    } else {
      // When the search is cleared, make sure to bring the current space back to the top.
      if (currentSpaceObject && currentSpaceIndex > -1) {
        filteredSpaces.unshift(currentSpaceObject);
      }
    }

    // Remove duplicates that might occur by adding the current or selected space.
    return filteredSpaces.filter((space, index, self) =>
      index === self.findIndex(s => s.space_uuid === space.space_uuid)
    );
  }
  },
  methods: {
    ...mapActions({
      switchSpace: "accounts/switchSpace",
      toggleAccountModal: "accounts/toggleAccountModal",
    }),
    clearSpaceFilters() {
      localStorage.removeItem('checkLocations');
      localStorage.removeItem('checkDeviceTags');
      localStorage.removeItem('checkLocationType');
      localStorage.removeItem('checkCountry');
      localStorage.removeItem('checkLocationTags');
    },
    async logout() {
      const current = this.user.spaces.find(
        (space) => space.space_uuid === this.currentSpace
      );
      const accountUuid = (current || this.user.spaces[0])?.account_uuid;
      this.$store.dispatch("iam/logoutUser", accountUuid);
    }
  },
  watch: {
    currentSpace(newVal) {
      if (newVal && newVal !== this.selectedSpace) {
        this.selectedSpace = newVal;
      }
    },
    selectedSpace(newVal, oldVal) {
      // Ignore initial bind and no-op reselection of the already active space.
      if (!newVal || newVal === oldVal || newVal === this.currentSpace) {
        return;
      }

      this.switchSpace(newVal).then(() => {
        this.clearSpaceFilters();
      }).catch((error) => {
        console.error("Failed to switch space", error);
        this.selectedSpace = this.currentSpace;
      });
    }
  },
  mounted() {
    this.selectedSpace = this.currentSpace;
  }
}
</script>

<style scoped lang="scss">
@supports (-moz-appearance:none) {
  .firefox-container {
    margin-bottom: 100px;
  }
}
.account-modal {
  width: 336px;
  position: fixed;
  right: 0px;
  z-index: 9999;
  /* Izmjene za Flexbox: */
  display: flex;
  flex-direction: column;
  /* Osigurava da se modal ne proteže izvan visine zaslona, uz mali odmak */
  max-height: 100%;
  /* Osigurava da modal ima barem minimalnu visinu za prikaz fiksnih elemenata */
  min-height: fit-content;
   max-height: calc(150vh - 100px);
}
.scrollable-card {
  /* Omogućuje skrolanje za sadržaj liste */
  overflow-y: auto; 
  /* Omogućuje ovom dijelu da se dinamički popunjava preostali prostor */
  flex-grow: 1; 
  /* Važno: Postavite visinu na nulu kako bi flexbox ispravno izračunao slobodan prostor */
  flex-basis: 0;
  scrollbar-width: none;
}
.fixed-sign-out {
  /* Osigurava da dugme ostaje fiksirano na dnu */
  position: sticky;
  bottom: 0;
  z-index: 2;
  border-top: 1px solid var(--v-blackGray-base);
  background: var(--v-background-base);
}
.avatar {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  border: solid 1px rgba(34, 34, 34, 0.2);
}
.email {
  color: var(--v-blackGray-base);
}
.sign-out-btn {
  font-size: 14px !important;
  width: 80px !important;
  height: 24px !important;
  border-color: var(--v-lineColor-base);
}
.scrollable-content {
  position: relative;
  display: flex;
  flex-direction: column;
  height: auto;
  /* Omogućuje ovom dijelu da se dinamički širi i popunjava preostali prostor */
  flex-grow: 1;
  /* Ključno za ispravno ponašanje `overflow` unutar flex kontejnera */
  min-height: 0;
}
.fixed-search-bar {
  position: sticky;
  top: 0;
  z-index: 1;
  /* Dodana pozadina za pokrivanje scroll-a, slično kao i za sign-out gumb */
  background: var(--v-bgdModal-base);
  height: 40px;
  margin-bottom: 10px;
  
}
.scrollable-card {
  max-height: calc(94vh - 100px); /* Set a maximum height */
  overflow-y: auto;  /* Enable vertical scrolling */
}
.fixed-sign-out {
  /* Osigurava da gumb ostaje fiksiran na dnu modaala */
  position: sticky;
  bottom: 0;
  /* Povećan z-index kako bi prekrio sve ostale elemente prilikom scroll-a */
  z-index: 2;
  border-top: 1px solid var(--v-lineColor-base) !important;
  /* Ključno: Dodana pozadina kako bi izgledalo kao dio kartice i prekrilo sadržaj ispod */
  background: var(--v-bgdModal-base);
  align-items: flex-end;
}

.space-avatar {
  border-radius: 4px;
  border: solid 1px rgba(34, 34, 34, 0.2);
}

.custom-radio .v-input--selection-controls__ripple {
  background-color: transparent !important;
  border-color: transparent !important;
}

.custom-radio .v-input--selection-controls__input:checked + .v-input--selection-controls__ripple {
  background-color: var(--v-primary-base) !important;
  border-color: var(--v-primary-base) !important;
}

.custom-radio .v-input--selection-controls__input:checked + .v-input--selection-controls__ripple::before {
  border-color: var(--v-primary-base) !important;
}

.custom-radio .v-input--selection-controls__input:hover + .v-input--selection-controls__ripple {
  background-color: var(--v-primary-base) !important;
}
.initials-avatar {
  font-size: 12px;       /* shrink initials */
  font-weight: 600;      /* optional: make them bolder */
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

</style>